import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from "@angular/core";
import { DecimalPipe, NgTemplateOutlet } from "@angular/common";

import { HomologacionConsultaService } from "../homologacion-consulta.service";
import { RadarRubrosComponent, SerieRadar } from "../radar-rubros/radar-rubros.component";
import { ResultadoResumidoComponent } from "../resultado-resumido/resultado-resumido.component";
import {
  coletilla,
  dictamenClase,
  dictamenTexto,
  fechaDmy,
  num,
  veredictoClase,
  veredictoTexto,
} from "../homologacion-formato";
import {
  HomologacionComparado,
  HomologacionComparativa,
  HomologacionConsultaRes,
  HomologacionFicha,
  HomologacionFichaFranja,
  HomologacionFilaComparada,
  HomologacionResumen,
  HomologacionVara,
  HomologacionVentaja,
  TipoVentaja,
} from "../../shared/model/iso42001/homologacion-modelo.model";

/** Las ventajas de una configuración de un modelo, para su tarjeta de «En qué es mejor cada uno». */
interface Tarjeta {
  modelo: HomologacionComparado;
  ventajas: HomologacionVentaja[];
}

/**
 * La comparativa entre modelos (spec 6.6): ¿en qué es mejor cada uno?
 *
 * Solo se compara lo medido con la misma vara (rúbrica, suite y prompts). Los
 * líderes y sus márgenes los decide el servidor con los umbrales de la spec
 * (1.0 punto; 10 % en costo y latencia): la pantalla no recalcula nada, y lo
 * que no pasa el umbral lo dice como Equivalentes, sin presumirlo.
 *
 * La usan la pestaña Comparativa de la gestión y la pantalla de consulta; lo
 * que hace «Ver resultado» lo decide quien la usa (evento abrir).
 *
 * Al final, aparte, la franja «Según el catálogo» (spec 5.8): los números de
 * la ficha vigente de cada modelo. No ordena, no califica y no entra en «En
 * qué es mejor cada uno».
 */
@Component({
  selector: "app-comparativa-modelos",
  standalone: true,
  imports: [DecimalPipe, NgTemplateOutlet, RadarRubrosComponent, ResultadoResumidoComponent],
  templateUrl: "./comparativa.component.html",
  styleUrls: ["../homologacion-comun.css", "./comparativa.component.css"],
})
export class ComparativaComponent implements OnInit {
  /** Pidieron ver el resultado de una evaluación. */
  @Output() abrir = new EventEmitter<number>();

  /** Hasta cuántos modelos van encima en el radar, para que se lea. */
  readonly MAX_RADAR = 5;
  /** Tokens de homologacion-comun.css: cada tema les da sus colores. */
  private readonly COLORES = ["var(--hom-serie-1)", "var(--hom-serie-2)", "var(--hom-serie-3)", "var(--hom-serie-4)", "var(--hom-serie-5)"];

  readonly num = num;
  readonly veredictoTexto = veredictoTexto;
  readonly veredictoClase = veredictoClase;
  readonly dictamenTexto = dictamenTexto;
  readonly dictamenClase = dictamenClase;
  readonly coletilla = coletilla;
  readonly fechaDmy = fechaDmy;

  varas: HomologacionVara[] = [];
  vara: HomologacionVara | null = null;
  comparativa: HomologacionComparativa | null = null;
  cargandoVaras = false;
  cargando = false;
  error = "";

  tarjetas: Tarjeta[] = [];
  equivalentes: HomologacionVentaja[] = [];
  /** Las evaluaciones (una por modelo y configuración) que van en el radar. */
  enRadar = new Set<number>();
  etiquetasRadar: string[] = [];
  seriesRadar: SerieRadar[] = [];
  /** Con un solo modelo en la vara: su Resultado resumido, abajo del aviso. */
  unico: HomologacionResumen | null = null;
  /**
   * Y la ficha del catálogo vigente de ese modelo (consulta/ficha.do, de
   * columnas): con una sola configuración no hay franja. null si no tiene o
   * si falla: el resumen se ve igual, sin el bloque.
   */
  fichaUnica: HomologacionFicha | null = null;
  maxCapacidad = 100;
  /** La ficha vigente de cada modelo (por idModelo): la franja «Según el catálogo». No ordena ni califica. */
  franja = new Map<number, HomologacionFichaFranja>();
  /** «22/09/2026», o «del 01/09/2026 al 22/09/2026» si no se verificaron el mismo día. */
  verificadoFranja = "";

  constructor(
    private service: HomologacionConsultaService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cargarVaras();
  }

  cargarVaras(): void {
    this.cargandoVaras = true;
    this.error = "";
    this.service.varas().subscribe({
      next: (res) => {
        this.cargandoVaras = false;
        if (res.codigoError) {
          this.error = this.mensaje(res, "No se pudieron consultar las varas.");
          this.pinta();
          return;
        }
        this.varas = res.varas || [];
        // Por omisión, la del vigente; si no hay vigente, la más reciente.
        const inicial = this.varas.find((v) => v.delVigente) || this.varas[0] || null;
        if (inicial) {
          this.elegir(inicial);
        }
        this.pinta();
      },
      error: () => {
        this.cargandoVaras = false;
        this.error = "No se pudieron consultar las varas: sin conexión con el servidor.";
        this.pinta();
      },
    });
  }

  claveVara(v: HomologacionVara): string {
    return `${v.hashRubrica}|${v.hashSuite}|${v.hashPrompts}`;
  }

  textoVara(v: HomologacionVara): string {
    const rubrica = [v.perfilRubrica, v.versionRubrica ? `v${v.versionRubrica}` : ""].filter((x) => !!x).join(" ");
    return `${rubrica ? rubrica + " · " : ""}rúbrica ${v.hashRubrica} · suite ${v.hashSuite} · prompts ${v.hashPrompts}`
      + ` · ${v.modelos} ${v.modelos === 1 ? "configuración" : "configuraciones"}${v.delVigente ? " · la del vigente" : ""}`;
  }

  alElegirVara(evento: Event): void {
    const clave = (evento.target as HTMLSelectElement).value;
    const v = this.varas.find((x) => this.claveVara(x) === clave);
    if (v && v !== this.vara) {
      this.elegir(v);
    }
  }

  private elegir(v: HomologacionVara): void {
    this.vara = v;
    this.comparativa = null;
    this.unico = null;
    this.fichaUnica = null;
    this.cargando = true;
    this.error = "";
    this.service.comparativa(v).subscribe({
      next: (res) => {
        if (this.vara !== v) {
          return;
        }
        this.cargando = false;
        if (res.codigoError || !res.comparativa) {
          this.error = this.mensaje(res, "No se pudo armar la comparativa.");
          this.pinta();
          return;
        }
        this.arma(res.comparativa);
        this.pinta();
      },
      error: () => {
        if (this.vara === v) {
          this.cargando = false;
          this.error = "No se pudo armar la comparativa: sin conexión con el servidor.";
          this.pinta();
        }
      },
    });
  }

  /** Lo que se pinta sale tal cual del servidor; aquí solo se agrupa para leerlo. */
  private arma(c: HomologacionComparativa): void {
    this.comparativa = c;
    const modelos = c.modelos || [];
    this.franja = new Map((c.franja || []).filter((f) => !!f && f.idModelo != null).map((f) => [f.idModelo, f]));
    // El mismo orden que sort() sin comparador (carácter por carácter): yyyy-MM-dd de la más vieja a la más nueva.
    const dias = [...new Set([...this.franja.values()].map((f) => f.verificado).filter((v): v is string => !!v))]
      .sort((a, b) => Number(a > b) - Number(a < b));
    this.verificadoFranja = !dias.length ? ""
      : dias.length === 1 ? fechaDmy(dias[0]) : `del ${fechaDmy(dias[0])} al ${fechaDmy(dias[dias.length - 1])}`;
    this.tarjetas = modelos.map((m) => ({
      modelo: m,
      ventajas: (c.ventajas || []).filter((v) => !v.equivalentes && v.idEvalLider === m.idEval),
    }));
    this.equivalentes = (c.ventajas || []).filter((v) => v.equivalentes);
    // El radar arranca con los primeros cinco (el vigente, si está, siempre adentro).
    const vigente = modelos.find((m) => m.vigente);
    const iniciales = [...(vigente ? [vigente] : []), ...modelos.filter((m) => m !== vigente)].slice(0, this.MAX_RADAR);
    this.enRadar = new Set(iniciales.map((m) => m.idEval));
    this.armaRadar();
    this.maxCapacidad = Math.max(100, ...modelos.map((m) => num(m.capacidad) ?? 0));
    if (modelos.length === 1) {
      this.cargarUnico(modelos[0].idEval);
    }
  }

  private cargarUnico(idEval: number): void {
    const c = this.comparativa;
    this.service.resultado(idEval).subscribe({
      next: (res) => {
        if (this.comparativa === c && !res.codigoError && res.resumen) {
          this.unico = res.resumen;
          this.cargarFichaUnica(res.resumen.idModelo, c);
          this.pinta();
        }
      },
    });
  }

  /** La ficha vigente del modelo del resumen único. Sin ficha, o si falla: el resumen se ve igual, sin el bloque. */
  private cargarFichaUnica(idModelo: number, c: HomologacionComparativa | null): void {
    this.service.ficha(idModelo).subscribe({
      next: (res) => {
        if (this.comparativa === c && !res.codigoError) {
          this.fichaUnica = res.ficha || null;
          this.pinta();
        }
      },
      error: () => {
        if (this.comparativa === c) {
          this.fichaUnica = null;
          this.pinta();
        }
      },
    });
  }

  alternarRadar(m: HomologacionComparado): void {
    if (this.enRadar.has(m.idEval)) {
      if (this.enRadar.size > 1) {
        this.enRadar.delete(m.idEval);
      }
    } else if (this.enRadar.size < this.MAX_RADAR) {
      this.enRadar.add(m.idEval);
    }
    this.armaRadar();
  }

  private armaRadar(): void {
    const c = this.comparativa;
    if (!c) {
      return;
    }
    const rubros = c.rubros || [];
    this.etiquetasRadar = rubros.map((r) => r.nombre);
    this.seriesRadar = (c.modelos || [])
      .filter((m) => this.enRadar.has(m.idEval))
      .map((m) => ({
        nombre: this.etiqueta(m),
        valores: rubros.map((r) => this.valor(r, m)),
        color: this.color(m),
      }));
  }

  /** El color de un modelo es siempre el mismo, lo elija o no para el radar. */
  color(m: HomologacionComparado): string {
    const i = (this.comparativa?.modelos || []).indexOf(m);
    return this.COLORES[(i < 0 ? 0 : i) % this.COLORES.length];
  }

  valor(f: HomologacionFilaComparada, m: HomologacionComparado): number | null {
    return num(f.valores ? f.valores[String(m.idEval)] : null);
  }

  /** La ventaja del encabezado (capacidad, costo o latencia) si la tiene este modelo. */
  lider(tipo: TipoVentaja, m: HomologacionComparado): HomologacionVentaja | null {
    return (this.comparativa?.ventajas || []).find((v) => v.tipo === tipo && !v.equivalentes && v.idEvalLider === m.idEval)
      || null;
  }

  /** Cómo se nombra una entrada donde no cabe más: su configuración, que ya dice el modelo y el effort. */
  etiqueta(m: HomologacionComparado): string {
    return m.nombreConfiguracion?.trim() || m.nombre;
  }

  nombreDe(idEval?: number | null): string {
    const m = (this.comparativa?.modelos || []).find((x) => x.idEval === idEval);
    return m ? this.etiqueta(m) : "—";
  }

  /** «+1.9 pts» o «22 % menos». */
  margen(v: { margen?: number | null; unidad?: string }, unidad: string = v.unidad || "pts"): string {
    const n = num(v.margen);
    if (n === null) {
      return "";
    }
    return unidad === "%" ? `${Math.round(n * 100)} % menos` : `+${n.toFixed(1)} pts`;
  }

  tipoTexto(t: TipoVentaja): string {
    switch (t) {
      case "RUBRO":
        return "Rubro";
      case "TAREA":
        return "Tarea";
      default:
        return "";
    }
  }

  /**
   * Las tablas por rubro y por tarea llevan una columna por configuración:
   * con muchas, se deslizan dentro de su caja en vez de aplastar los números.
   */
  get anchoFilas(): number {
    return 220 + 110 * (this.comparativa?.modelos.length || 0) + 170;
  }

  /** La franja del catálogo: una columna por configuración y ninguna de «Quién gana». */
  get anchoFranja(): number {
    return 220 + 110 * (this.comparativa?.modelos.length || 0);
  }

  barra(m: HomologacionComparado): number {
    const n = num(m.capacidad);
    return n === null ? 0 : Math.max(0, Math.min(100, (n / this.maxCapacidad) * 100));
  }

  /** Vuelve a pedir la comparativa de la vara elegida (o las varas, si ni eso cargó). */
  reintentar(): void {
    if (this.vara) {
      this.elegir(this.vara);
    } else {
      this.cargarVaras();
    }
  }

  /** La app corre sin zonas: una respuesta HTTP no repinta sola. */
  private pinta(): void {
    this.cdr.markForCheck();
  }

  private mensaje(res: HomologacionConsultaRes | null | undefined, porOmision: string): string {
    if (res && res.codigoError === "AUTH-001") {
      return "Tu sesión caducó. Vuelve a entrar.";
    }
    return (res && res.mensajeError) || porOmision;
  }
}
